
import { supabase } from "../../client";
import { sendRescheduleConfirmation } from "@/lib/email";
import { BookingEmailInfo, ClassEmailInfo } from "../../../supabase/types";
import { UpdatePaymentStatusResult, RescheduleBookingResult } from "./types";

// Update booking payment status
export const updateBookingPaymentStatus = async (
  bookingId: string,
  paymentStatus: string,
  adminId?: string
): Promise<UpdatePaymentStatusResult> => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({ payment_status: paymentStatus })
      .eq("id", bookingId);

    if (error) {
      console.error("Error updating payment status:", error);
      return { success: false, error: error.message };
    }

    // Log the admin action if adminId is provided
    if (adminId) {
      await supabase.from("admin_logs").insert({
        admin_id: adminId,
        action: "update_payment_status",
        booking_id: bookingId,
        details: `Updated payment status to ${paymentStatus}`,
      });
    }

    return { success: true };
  } catch (error: any) {
    console.error("Error in updateBookingPaymentStatus:", error);
    return { success: false, error: error.message };
  }
};

// Reschedule a booking to a different class
export const rescheduleBooking = async (
  bookingId: string,
  oldClassId: string,
  newClassId: string,
  adminId?: string
): Promise<RescheduleBookingResult> => {
  try {
    // 1. First get the booking details
    const { data: booking, error: bookingError } = await supabase
      .from("bookings")
      .select("id, user_id, class_id, first_name, last_name, email, group_size")
      .eq("id", bookingId)
      .single();

    if (bookingError || !booking) {
      console.error("Error fetching booking:", bookingError);
      return {
        success: false,
        error: bookingError?.message || "Booking not found",
      };
    }

    // Check if this is an admin operation
    let isAdmin = false;
    if (adminId) {
      const { data } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("id", adminId)
        .single();
      
      isAdmin = data?.is_admin || false;
    }

    // If not an admin, check if the user owns this booking
    if (!isAdmin && booking.user_id !== adminId) {
      return {
        success: false,
        error: "You are not authorized to reschedule this booking",
      };
    }

    // 2. Get details of the original class
    const { data: originalClass, error: originalClassError } = await supabase
      .from("classes")
      .select("id, title, day, date, time, duration")
      .eq("id", oldClassId)
      .single();

    if (originalClassError || !originalClass) {
      console.error("Error fetching original class:", originalClassError);
      return {
        success: false,
        error: originalClassError?.message || "Original class not found",
      };
    }

    // 3. Get details of the new class
    const { data: newClass, error: newClassError } = await supabase
      .from("classes")
      .select("id, title, day, date, time, duration, availablespots")
      .eq("id", newClassId)
      .single();

    if (newClassError || !newClass) {
      console.error("Error fetching new class:", newClassError);
      return {
        success: false,
        error: newClassError?.message || "New class not found",
      };
    }

    // 4. Check if the new class has enough spots
    const groupSize = booking.group_size || 1;
    if (newClass.availablespots < groupSize) {
      return {
        success: false,
        error: `Not enough spots available in the new class. Need ${groupSize}, but only ${newClass.availablespots} available.`,
      };
    }

    // 5. Update the booking with the new class ID
    const { error: updateError } = await supabase
      .from("bookings")
      .update({ class_id: newClassId })
      .eq("id", bookingId);

    if (updateError) {
      console.error("Error updating booking:", updateError);
      return { success: false, error: updateError.message };
    }

    // 6. Update spots in both classes
    const { error: incrementError } = await supabase.rpc(
      "increment_class_spots",
      { class_id: oldClassId, spots_to_add: groupSize }
    );

    if (incrementError) {
      console.error("Error incrementing original class spots:", incrementError);
    }

    const { error: decrementError } = await supabase.rpc(
      "decrement_class_spots",
      { class_id: newClassId, spots_to_remove: groupSize }
    );

    if (decrementError) {
      console.error("Error decrementing new class spots:", decrementError);
    }

    // 7. Send confirmation email
    const bookingInfo: BookingEmailInfo = {
      id: booking.id,
      firstName: booking.first_name,
      lastName: booking.last_name,
      email: booking.email,
      groupSize: groupSize,
    };

    const originalClassInfo: ClassEmailInfo = {
      title: originalClass.title,
      day: originalClass.day,
      date: originalClass.date,
      time: originalClass.time,
      duration: originalClass.duration,
    };

    const newClassInfo: ClassEmailInfo = {
      title: newClass.title,
      day: newClass.day,
      date: newClass.date,
      time: newClass.time,
      duration: newClass.duration,
    };

    try {
      await sendRescheduleConfirmation(bookingInfo, originalClassInfo, newClassInfo);
    } catch (emailError) {
      console.error("Error sending reschedule confirmation email:", emailError);
      // Don't fail the operation if email sending fails
    }

    // 8. Log the admin action if adminId is provided
    if (adminId) {
      await supabase.from("admin_logs").insert({
        admin_id: adminId,
        action: "reschedule_booking",
        booking_id: bookingId,
        details: `Rescheduled from class ${oldClassId} to ${newClassId}`,
      });
    }

    return { success: true };
  } catch (error: any) {
    console.error("Error in rescheduleBooking:", error);
    return { success: false, error: error.message };
  }
};
